// Keep this frontend mirror in sync with transactions-backend/functions/utils.py.
// The dashboard cannot import Python Lambda code across the FE/BE boundary.
export const OFFLINE_PAYMENT_METHODS = [
  'CASH',
  'CHECK',
  'MONEY_ORDER',
  'IN_KIND',
  'STOCK',
  'DONOR_ADVISED_FUND',
  'CRYPTO',
  'PAYPAL',
  'VENMO',
  'OTHER',
  'OFFLINE',
  'FREE',
] as const;

export const isOfflinePaymentMethod = (paymentMethod?: string | null) =>
  OFFLINE_PAYMENT_METHODS.includes(
    paymentMethod?.toUpperCase() as (typeof OFFLINE_PAYMENT_METHODS)[number],
  );

export const formatOfflinePaymentMethod = (paymentMethod: string): string =>
  paymentMethod
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace('Paypal', 'PayPal');

export const getEventOfflinePaymentMethods = (isGroupTicketV2: boolean) =>
  OFFLINE_PAYMENT_METHODS.filter(
    (paymentMethod) =>
      paymentMethod !== 'FREE' && (!isGroupTicketV2 || paymentMethod !== 'MONEY_ORDER'),
  );

// PayPal ids that only a processed gift carries. Rows written before 2024-03-26
// have no paymentProcessor and only paypalAuthorizationId / paypalTransferID.
const PAYPAL_PROCESSED_ID_FIELDS = [
  'paypalCaptureID',
  'paypalOrderID',
  'paypalAuthorizationId',
  'paypalTransferID',
] as const;

type PaymentRecordLike =
  | ({
      paymentMethod?: string | null;
      paymentProcessor?: string | null;
    } & Partial<Record<(typeof PAYPAL_PROCESSED_ID_FIELDS)[number], string | null>>)
  | null
  | undefined;

// A gift processed online through PayPal also has paymentMethod PAYPAL or VENMO.
// It carries paymentProcessor PAYPAL or a PayPal id, keeps its fee and can
// only be refunded. Mirrors is_offline_donation_record in edit-donation.py.
export const isOfflinePaymentRecord = (payment: PaymentRecordLike) => {
  if (!payment) return false;
  if (String(payment.paymentProcessor || '').toUpperCase() === 'PAYPAL') return false;
  if (PAYPAL_PROCESSED_ID_FIELDS.some((field) => payment[field])) return false;
  return isOfflinePaymentMethod(payment.paymentMethod);
};

// The payment type an edit drawer opens a record under: the offline method (spaced,
// e.g. 'MONEY ORDER') for a really-offline record, `onlineType` for everything else.
// Record-aware on purpose: a PayPal or Venmo gift processed online carries the same
// paymentMethod, but it is an online gift, and opening it as offline makes the save
// send amount/fees/coverFees, which the backend then re-prices at card rates.
export const getEditPaymentType = (
  record: (PaymentRecordLike & { paymentMethod?: string | null }) | null | undefined,
  onlineType: string,
): string => {
  const paymentMethod = record?.paymentMethod;
  if (!paymentMethod) return onlineType;
  return isOfflinePaymentRecord(record) ? paymentMethod.replace('_', ' ') : onlineType;
};
